import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export function CreateCustomCourse({ isOpen, onClose, onCancel, initialTitle = '', onSuccess }) {
  const { company } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courseCreated, setCourseCreated] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  // Reset form whenever dialog opens or initialTitle changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: initialTitle,
        description: ''
      });
      setCourseCreated(false);
    }
  }, [isOpen, initialTitle]);

  // Handle dialog close
  const handleOpenChange = open => {
    if (!open) {
      // If closing without creating a course, call onCancel for cleanup
      if (!courseCreated && onCancel) {
        onCancel();
      }
      // Always call onClose to actually close the dialog
      onClose();
    }
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.title || !formData.title.trim()) {
        toast.error('Course title is required');
        return;
      }

      if (!company?.id) {
        toast.error('Company information is required');
        return;
      }

      setIsSubmitting(true);

      // Insert the custom course
      const { data, error } = await supabase
        .from('custom_courses')
        .insert({
          title: formData.title.trim(),
          description: formData.description?.trim() || null,
          company_id: company.id,
          created_date: new Date().toISOString(),
          updated_date: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Custom course created successfully');

      // Mark that course was successfully created
      setCourseCreated(true);

      // Call onSuccess with the created course data
      if (onSuccess && data) {
        onSuccess(data);
      }

      // Close the dialog (handleOpenChange will NOT call onCancel since courseCreated is true)
      onClose();
    } catch (error) {
      console.error('Error creating custom course:', error);
      toast.error('Failed to create custom course');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>Create Custom Course</DialogTitle>
          <DialogDescription>
            Create a custom course for your company. This course will only be visible to your organization.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          <div className='space-y-2'>
            <Label htmlFor='course-title'>
              Course Title <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='course-title'
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder='e.g., Advanced Fire Safety Training'
              autoFocus
            />
            <p className='text-xs text-muted-foreground'>
              Enter a clear, descriptive name for your custom course
            </p>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='course-description'>Description</Label>
            <Textarea
              id='course-description'
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder='Describe what this course covers, who it is for, and any key requirements...'
              rows={4}
              className='resize-none'
            />
            <p className='text-xs text-muted-foreground'>
              Optional: Provide details to help identify this course
            </p>
          </div>

          <div className='rounded-lg bg-blue-50 border border-blue-200 p-3'>
            <p className='text-sm text-blue-900'>
              <strong>Note:</strong> This custom course will only be visible to members of your company and can be used across all your projects.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Creating...
              </>
            ) : (
              'Create Course'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
